/**
 * The one place the UI asks "what may I do, and what may I see?".
 *
 * Everything comes from GET /admin/me/scopes, cached for a minute. Components
 * must not re-implement role checks: a screen that hides a control on `role`
 * lies the moment an admin grants or revokes a permission.
 *
 *   useCan('tasks.assign')  what the person may DO   (scopes)
 *   useIsAdmin()            owner or manager          (admin.panel scope)
 *   useTasksOnly()          sees only their own tasks (clinic access level)
 */
import { useQuery } from '@tanstack/react-query';
import { fetchMyScopes, qk, type MyScopes } from '@/lib/queries';
import { useAuth } from '@/hooks/use-auth';

export function useMyScopes() {
  const { user } = useAuth();
  return useQuery<MyScopes>({ queryKey: qk.myScopes, queryFn: fetchMyScopes, enabled: !!user, staleTime: 60_000 });
}

export function useCan(scope: string): boolean {
  const q = useMyScopes();
  return !!q.data?.scopes.includes(scope);
}

/** Owner or manager: sees every clinic and the admin surfaces. */
export function useIsAdmin(): boolean {
  const q = useMyScopes();
  return !!q.data?.is_admin;
}

/** True once we KNOW the person is own-tasks-only. Undefined while loading, so
 *  callers can avoid flashing the wrong layout. */
export function useTasksOnly(): boolean | undefined {
  const q = useMyScopes();
  if (!q.data) return undefined;
  return !q.data.unrestricted && q.data.access_level === 'tasks';
}
